/* eslint-disable */
import { format } from "date-fns";
import { DATE_FORMAT_CLIENT } from "constants/date-time";
import {
  EXISTING_USER,
  INDEX_DB,
  menuItems,
  ONBOARDING_DONE,
} from "constants/general";
import dayjs from "dayjs";
import { GERMAN_DATE_FORMAT } from "constants/date-time";
import { isAppleDevice } from "helpers/userAgent";
import { COUNTRY_SELECTION_STORAGE_NAME } from "constants/country";
import { iDbClearTable } from "@/index-db/db.helpers";
import { PLATFORM, type Platform } from "constants/general";
import {
  hasAppsFlyerConsent,
  triggerAppsFlyerSdkInit,
} from "./appsflyer-gtm.helper";

// select the browser storage
const appStorage = sessionStorage; //localStorage;

const cacheStorage = localStorage;
// Global queue for events waiting for OneTrust
const pendingEvents: DataLayerEvent[] = [];
let oneTrustReady = false;
/**
 * Get a value from browser storage
 * @param key
 * @returns string | null
 */
const getFromStorage = (key: string) => {
  return key && appStorage.getItem(key) ? appStorage.getItem(key) : null;
};

/**
 * Set a value to browser storage using a specified key
 * @param key
 * @param value
 */
const setToStorage = (key: string, value = "") => {
  if (key) {
    appStorage.setItem(key, value);
  }
};

/**
 * Get a value from browser storage
 * @param key
 * @returns string | null
 */
const getFromLocalStorage = (key: string) => {
  return key && cacheStorage.getItem(key) ? cacheStorage.getItem(key) : null;
};

/**
 * Set a value to browser storage using a specified key
 * @param key
 * @param value
 */
const setToLocalStorage = (key: string, value = "") => {
  if (key) {
    cacheStorage.setItem(key, value);
  }
};

/**
 * Remove a specific value or clear the entire browser storage
 * @param key
 */
const removeFromStorage = (key = "") => {
  if (key) {
    appStorage.removeItem(key);
  } else {
    appStorage.clear();
  }
};

/**
 * Remove a specific value or clear the entire browser storage
 * @param key - Single key to remove, or empty to clear all
 * @param keysToKeep - Array of keys to preserve when clearing all storage
 */
const defaultKeysToKeep = [ONBOARDING_DONE, EXISTING_USER];
const removeFromLocalStorage = (
  key = "",
  keysToKeep: string[] = defaultKeysToKeep,
) => {
  if (key) {
    cacheStorage.removeItem(key);
  } else {
    if (keysToKeep.length > 0) {
      // Save items we want to keep
      const itemsToKeep: { [key: string]: string } = {};
      keysToKeep.forEach((k) => {
        const value = cacheStorage.getItem(k);
        if (value !== null) {
          itemsToKeep[k] = value;
        }
      });

      // Clear all storage
      cacheStorage.clear();

      // Restore the items we want to keep
      Object.entries(itemsToKeep).forEach(([k, value]) => {
        cacheStorage.setItem(k, value);
      });
    } else {
      cacheStorage.clear();
    }
  }
};

/**
 * Clear specified IndexDB tables or default tables (questionnaires and consents)
 * @param tablesToClear - Array of table names to clear. If empty, clears questionnaires and consents tables.
 */
const defaultTablesToClean = [
  INDEX_DB.QUESTIONNAIRES_TABLE,
  INDEX_DB.CONSENTS_TABLE,
  INDEX_DB.OUR_CONDITIONS_TABLE,
  INDEX_DB.PROFILE_IMAGE_TABLE,
];
const clearIndexDBTables = (
  tablesToClear: string[] = defaultTablesToClean,
): void => {
  // Parallel: fire all at once, don't block on completion
  Promise.allSettled(
    tablesToClear.map((tableName) => iDbClearTable(tableName)),
  );
};

/**
 * Format given date
 * @param date
 * @param dateFormat
 * @returns
 */
const formatDateTime = (date = new Date(), dateFormat = DATE_FORMAT_CLIENT) => {
  return new Date(format(date, dateFormat));
};

/**
 * Format a date string to short display format e.g. "12 May"
 * @param dateStr - ISO date string or any value parseable by Date
 * @returns formatted string like "1 Jan" or "12 May"
 */
const formatDateShort = (dateStr: string): string => {
  return format(new Date(dateStr), "d MMM");
};

/**
 * Get time from date object in AM/PM format
 * @param date
 * @returns
 */
const formatAMPM = (dateObj: any) => {
  const twelve = 12;
  const ten = 10;
  const date = new Date(dateObj);
  let hours = date.getHours();
  let minutes = date.getMinutes();
  // hours = hours+1;
  const ampm = hours >= twelve ? "PM" : "AM";

  hours = hours % twelve;
  hours = hours ? hours : twelve; // the hour '0' should be '12'
  let minutes1 = minutes < ten ? "0" + minutes : minutes;
  let strTime = hours + ":" + minutes1 + " " + ampm;
  return strTime;
};
/**
 * format date and time; date in the format '2022-02-01T07:47:43'
 * @param date date value
 * @param isTimeshow result want to format time also; true-format date and time eg:01/02/2022 07:47:43 AM;false-format date only eg:06/01/2022
 * @returns formated date / time
 */
const formatTimeandDate = (date: string, isTimeshow: boolean) => {
  const twelve = 12;
  const ten = 10;
  const zero = 0;
  let newDate = new Date(date);
  if (!isTimeshow) {
    return newDate.toLocaleDateString("fr-CH");
  } else {
    let ampm;
    let hour: any;
    let second;
    let minute;
    if (newDate.getHours() >= twelve) {
      ampm = "PM";
    } else {
      ampm = "AM";
    }
    if (newDate.getHours() < ten) {
      if (newDate.getHours() == zero) hour = "12";
      else hour = "0" + newDate.getHours();
    } else {
      hour = newDate.getHours();
    }
    if (newDate.getMinutes() < ten) {
      minute = "0" + newDate.getMinutes();
    } else {
      minute = newDate.getMinutes();
    }
    if (newDate.getSeconds() < ten) {
      second = "0" + newDate.getSeconds();
    } else {
      second = newDate.getSeconds();
    }
    if (newDate.getHours() > twelve) {
      hour = newDate.getHours() - twelve;
    } else {
      if (newDate.getHours() == zero) hour = "12";
      else hour = newDate.getHours();
    }
    if (hour < ten) {
      if (hour == zero) hour = "12";
      else hour = "0" + hour;
    }
    let timeDate =
      newDate.toLocaleDateString("fr-CH") +
      " " +
      hour +
      ":" +
      minute +
      ":" +
      second +
      " " +
      ampm;
    // let timeDate = newDate.toLocaleDateString('fr-CH') + ' ' + (hour && hour >= 1 ? (hour-1) : hour) + ':' + minute + ':' + second + ' ' + ampm;
    return timeDate;
  }
};

/**
 * Split last character from string and return with space
 * @param key
 * @returns a value with type string
 */
const SplitLastCharacter = (key: string) => {
  let lastChar = key.substr(key.length - 1);
  let initial = key.slice(0, -1);
  return `${initial} ${lastChar}`;
};

/**
 * Split last character from string and return array
 * @param key
 * @returns a value with type string
 */
const SplitLastCharacterAsArray = (key: string) => {
  let lastChar = key.substr(key.length - 1);
  let initial = key.slice(0, -1).trim();
  return [initial, lastChar];
};

/**
 * Returns date in YYYY-MM-DD
 * @param date
 * @returns current date
 */
const formatDateToApi = (date: string) => {
  let newDate = new Date(date);
  return newDate.toLocaleDateString("fr-CA");
};

/**
 * get second parameter from current url
 * @return string
 */
const getUrlPathForMainMenu = () => {
  let currentUrlPath = window.location.pathname.split("/");
  // check if matches url
  for (const [key, value] of Object.entries(menuItems)) {
    if (
      currentUrlPath &&
      `/${currentUrlPath[1]}/${currentUrlPath[2]}` ===
        `${value?.to?.startsWith("/") ? "" : "/"}${value?.to?.toLowerCase()}`
    ) {
      return value.key;
    }
  }

  for (const [key, value] of Object.entries(menuItems)) {
    if (
      (currentUrlPath && currentUrlPath[1]) ===
      (value && value.key.toLowerCase())
    ) {
      return value.key;
    }
  }
};

const getUrlPathBasedOnIndex = (index: any) => {
  let currentUrlPath = window.location.pathname.split("/");
  if (Array.isArray(currentUrlPath) && currentUrlPath.length !== 0) {
    return currentUrlPath[index];
  } else {
    return currentUrlPath;
  }
};
/**
 *
 * @param date
 * @description get forst date and last date between two months
 * @returns
 */
const getFirstAndLastDateOfCustomMonths = (
  date: Date,
  numberOfMonth: number = 0,
) => {
  const subtractMonth = numberOfMonth === 0 ? 0 : numberOfMonth - 1;
  if (date && date instanceof Date) {
    return {
      start_date:
        date &&
        date instanceof Date &&
        dayjs(date)
          .subtract(subtractMonth, "month")
          .startOf("month")
          .format(GERMAN_DATE_FORMAT),
      end_date:
        date &&
        date instanceof Date &&
        dayjs(date).endOf("month").format(GERMAN_DATE_FORMAT),
    };
  } else {
    return {
      start_date: dayjs()
        .subtract(subtractMonth, "month")
        .startOf("month")
        .format(GERMAN_DATE_FORMAT),
      end_date: dayjs().endOf("month").format(GERMAN_DATE_FORMAT),
    };
  }
};
const getUserDataFromStorage = () => {
  try {
    const userDataFromStorage: any = getFromLocalStorage("recoil-persist");
    if (!userDataFromStorage) return null;

    const persistedData = JSON.parse(userDataFromStorage);
    return persistedData || null;
  } catch (error) {
    console.warn("Error parsing recoil-persist from localStorage:", error);
    return null;
  }
};
// Helper: flush queued events once OneTrust is ready
const flushPendingEvents = () => {
  if (!window.dataLayer) window.dataLayer = [];
  pendingEvents.forEach((event) => {
    window.dataLayer.push(event);
  });
  pendingEvents.length = 0; // Clear queue
};

// Watch for OneTrust initialization
const waitForOneTrust = () => {
  // If already ready, return early
  if (oneTrustReady) return;

  // Wrap the existing OptanonWrapper set in index.html
  const prevWrapper = (window as any).OptanonWrapper;
  (window as any).OptanonWrapper = () => {
    oneTrustReady = true;
    flushPendingEvents();
    // Trigger consent-gated GTM loading & AppsFlyer SDK init
    if (hasAppsFlyerConsent()) {
      triggerAppsFlyerSdkInit();
    }
    if (typeof prevWrapper === "function") prevWrapper();
  };

  // Optional: also handle consent change event
  if ((window as any).OneTrust?.OnConsentChanged) {
    (window as any).OneTrust.OnConsentChanged(() => {
      if (!oneTrustReady) {
        oneTrustReady = true;
        flushPendingEvents();
      }
      if (hasAppsFlyerConsent()) {
        console.log(
          "AppsFlyer consent granted, triggering SDK init if not already done",
        );
        triggerAppsFlyerSdkInit();
      }
    });
  }
};

// Define the shape of the dataLayer push object
export interface DataLayerEvent {
  event?: string; // Event name (required)
  [key: string]: any; // Allow additional properties with any value
}

// Function to safely push items to the dataLayer with types
// const pushToDataLayer = <T extends DataLayerEvent>(
//   data: T,
//   isUrlNeeded: boolean = true
// ): void => {
//   // Ensure dataLayer is initialized before pushing
//   window.dataLayer = window.dataLayer || []; // Fallback if undefined
//   // Push the event data into the GA dataLayer
//   window.dataLayer.push({
//     ...data,
//     ...(isUrlNeeded ? { url: window.location.href } : {}),
//   });
// };
const pushToDataLayer = <T extends DataLayerEvent>(
  data: T,
  isUrlNeeded: boolean = true,
): void => {
  window.dataLayer = window.dataLayer || [];

  const eventData = {
    ...data,
    ...(isUrlNeeded ? { url: window.location.href } : {}),
  };

  if (oneTrustReady) {
    window.dataLayer.push(eventData);
  } else {
    pendingEvents.push(eventData);
  }
};

// Define an interface for the stored item with expiration
interface ExpirableItem<T> {
  value: T;
  expiration: number; // Timestamp when the item expires
}

// Function to set an item with an expiration time (in milliseconds)
function setItemWithExpiration<T>(
  key: string,
  value: T,
  expirationTime: number,
): void {
  const expirationTimestamp = new Date().getTime() + expirationTime;
  const item: ExpirableItem<T> = {
    value: value,
    expiration: expirationTimestamp,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

// Function to get an item, checking if it has expired
function getItemWithExpiration<T>(key: string): T | null {
  const itemString = localStorage.getItem(key);
  if (itemString) {
    const item: ExpirableItem<T> = JSON.parse(itemString);
    const currentTimestamp = new Date().getTime();
    if (currentTimestamp > item.expiration) {
      localStorage.removeItem(key); // Item expired, remove it
      return null;
    } else {
      return item.value; // Return the value if not expired
    }
  }
  return null; // Return null if the item doesn't exist
}

/**
 * Set cookie with expiry
 * @param key Cookie name
 * @param value Cookie value
 * @param expiry Expiry in milliseconds
 * @param path Path
 */
export const setCookieItem = (
  key: string,
  value: any,
  expiry: number,
  path = "/",
) => {
  const cookieExpiry = new Date(Date.now() + expiry).toUTCString();
  // Encode the cookie value to handle special characters and store it
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${cookieExpiry}; path=${path}; SameSite=Lax`;
};

/**
 * Get cookie value
 * @param key Cookie name
 * @returns Cookie value if exists; null otherwise
 */
export const getCookieItem = (key: string) => {
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(key))
    ?.split("=")[1];
  // Decode the cookie value since it was encoded when setting
  return cookieValue ? decodeURIComponent(cookieValue) : null; // Null if cookie is not available
};

/**
 * Delete a cookie by name
 * @param key Cookie name
 * @param path Path (default: "/")
 */
export const deleteCookieItem = (key: string, path = "/") => {
  document.cookie = `${key}=; max-age=-1; path=${path}; SameSite=Lax`;
};

// Function to escape special characters in the placeholder for RegExp
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^=!:${}()|\[\]\/\\]/g, "\\$&"); // Escapes special characters for RegExp
}

// Function to replace all instances of a placeholder in a string
function replacePlaceholder(
  str: string,
  placeholder: string,
  replacement: string,
): string {
  const escapedPlaceholder = escapeRegExp(placeholder);
  return str.replace(new RegExp(escapedPlaceholder, "g"), replacement);
}

//remove first item from string
function removeFirstChar(item: string) {
  return item.substring(1);
}
//remove last item from string
function removeLastChar(str: string) {
  return str?.slice(0, -1);
}
//check standalone mode
const isStandalone = (): boolean => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true || // type assertion to bypass TS error
    document.referrer.startsWith("android-app://")
  );
};
//check whether the device is iphone - push notification
const isIphoneDevice = (deviceModel: any): number =>
  deviceModel === "iPhone" ? 1 : 0;
//check push notificaiton permission
const requestNotificationPermission = () => {
  const isDeviceApple = isAppleDevice();
  const isStandAlone = isStandalone();
  return isDeviceApple && isStandAlone
    ? "default"
    : isDeviceApple
      ? window.Notification.requestPermission().then((permission) => {
          return permission; // "granted", "denied", or "default"
        })
      : window.Notification.requestPermission();
};

// hard reload and navigate
const redirectTo = (url: string) => {
  window.location.href = url;
};
//check given url is valid or not
const isValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    // Check if it's a web URL and not an internal reference
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (e) {
    return false;
  }
};

/**
 * Scrolls to the element with the given ID.
 * @param id - The ID of the element to scroll to.
 * @param behavior - Scroll behavior: 'auto' or 'smooth' (default is 'smooth').
 * @param block - Vertical alignment: 'start', 'center', 'end', or 'nearest' (default is 'start').
 */
const scrollToElementById = (
  id: string,
  behavior: ScrollBehavior = "smooth",
  block: ScrollLogicalPosition = "start",
): void => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior, block });
  }
};

/**
 * Detects the current platform based on user agent and environment
 * @returns Platform type: 'ios', 'android', or 'web'
 */
const detectPlatform = (): Platform => {
  // Server-side safety check
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return PLATFORM.WEB;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS WebView wrapper
  // Common indicators: iOS + (standalone mode or custom webkit)
  const isIOS = isAppleDevice();
  const isStandaloneMode = isStandalone();

  // iOS wrapper detection: iOS device in standalone/fullscreen mode
  if (isIOS && isStandaloneMode) {
    return PLATFORM.IOS;
  }

  // Check for Android
  // Bubblewrap typically runs in a TWA (Trusted Web Activity) context
  const isAndroid = /android/.test(userAgent);
  if (isAndroid) {
    return PLATFORM.ANDROID;
  }

  // Default to web for desktop and other platforms
  return PLATFORM.WEB;
};

waitForOneTrust();
export {
  getFromStorage,
  setToStorage,
  removeFromStorage,
  formatDateTime,
  formatAMPM,
  formatTimeandDate,
  SplitLastCharacter,
  formatDateToApi,
  SplitLastCharacterAsArray,
  setToLocalStorage,
  getFromLocalStorage,
  removeFromLocalStorage,
  clearIndexDBTables,
  getUrlPathForMainMenu,
  getUrlPathBasedOnIndex,
  getFirstAndLastDateOfCustomMonths,
  setItemWithExpiration,
  getItemWithExpiration,
  replacePlaceholder,
  removeFirstChar,
  removeLastChar,
  isStandalone,
  requestNotificationPermission,
  redirectTo,
  pushToDataLayer,
  isIphoneDevice,
  isValidUrl,
  scrollToElementById,
  detectPlatform,
  formatDateShort,
};
